import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CheckboxGroup,
  FormControl,
  Inline,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import {
  type IFuelTypes,
  PlanFuelSelection,
  removePlanSolar,
  setPlanFuel,
  setPlanSolar,
  useUserContext,
  YesNoE,
} from "@origin-digital/sales-common";
import {
  errorForm,
  logger,
  interaction,
} from "@origin-digital/reporting-client";
import { isErgon, isErgonSolar } from "@origin-digital/sales-common-types-fns";
import {
  EnergyAddressLookup,
  useEnergyAddressLookup,
  NMIResolutionState,
  type ConnectionAddressObject,
} from "@origin-digital/sales-energy-address-lookup";
import { FeatureContextReact } from "@origin-digital/features";
import styled from "styled-components";

import {
  BUTTON_CP,
  BUTTON_SALES,
  LEGACY_MOVE_CONTENT,
  FEATURE_SIMPLE_MOVE_REDIRECT_TO_CUSTOMISATION_APP,
  FORM_NAME,
  FUEL_DUAL,
  FUEL_ELEC,
  FUEL_GAS,
  INTERNET,
  LPG,
} from "src/consts";
import { ILegacyLookup } from "src/consts/interfaces";
import { isFeatureEnabled } from "src/utils/features";
import {
  getCheckboxErrorMessage,
  getDestinationURL,
  getDestinationURLWhenNoAddress,
  getFuelName,
  getSelectedProducts,
  isLPGOnlySelected,
} from "src/utils/utils";
import { CampaignPlans, getCampaignPlans } from "src/utils/campaignPlansAPI";
import { createSalesCart } from "src/utils/cartAPI";
import { Buttons } from "../../../components/Buttons/Buttons";
import { Disclaimer } from "../../../components/Disclaimer";
import {
  DismissableBanner,
  DismissableBannerType,
  IDismissableBannerOptions,
} from "../../../components/DismissableBanner/DismissableBanner";
import { ErgonPatchAlert } from "../../../components/ErgonPatchAlert/ErgonPatchAlert";
import {
  disableLPGSelection,
  getLPGOffer,
  LPGOfferDeliverability,
  setLPGStates,
} from "../../../utils/lpgOffersAPI";
import {
  getAuthMoveURL,
  getSalesCustomisationURL,
} from "../../../utils/externalRoutes";
import { LookupErrorAlert } from "./LookupErrorAlert";

const StackWithWordpressOverrides = styled(Stack)`
  input {
    box-sizing: content-box;
  }
`;

const PLAN_FUEL_BY_CART_FUEL: Record<IFuelTypes, PlanFuelSelection> = {
  dual: FUEL_DUAL,
  electricity: FUEL_ELEC,
  gas: FUEL_GAS,
};

export const LegacyLookup: React.FC<ILegacyLookup> = ({
  buttonLabelCP,
  buttonLabelSales,
  selectFuelCheckboxes,
}) => {
  const [fuelValue, setFuelValue] = useState<PlanFuelSelection | "">("");
  const [fuelError, setFuelError] = useState<string>("");
  const [dismissableBannerOptions, setDismissableBannerOptions] =
    useState<IDismissableBannerOptions>({
      type: DismissableBannerType.HIDE,
    });
  const [internetValue, setInternetValue] = useState<boolean>(false);
  const [lpgValue, setLPGValue] = useState<boolean>(false);
  const [lpgOfferDeliverability, setLpgOfferDeliverability] = useState<
    LPGOfferDeliverability | undefined
  >(undefined);
  const [lpgEnabled, setLPGEnabled] = useState<boolean>(true);
  const isMoverPage = true;

  const [campaignPlans, setCampaignPlans] = useState<CampaignPlans>();
  const [isErgonAddress, setIsErgonAddress] = useState<boolean>(false);
  const [isErgonSolarPatch, setIsErgonSolarPatch] = useState<boolean>(false);

  const [hasSubmitted, setHasSubmitted] = useState<boolean>(false);

  const { userProfile } = useUserContext();
  const featureContext = useContext(FeatureContextReact);
  const customisationRedirectEnabled = isFeatureEnabled(
    featureContext,
    FEATURE_SIMPLE_MOVE_REDIRECT_TO_CUSTOMISATION_APP
  );

  const [globalError, setGlobalError] = useState<boolean>(false);

  const {
    selectedAddress,
    unconfirmedAddress,
    nmiResolutionState,
    setSelectedEnergy,
    isAddressLookupInProgress,
  } = useEnergyAddressLookup();

  useEffect(() => {
    if (userProfile) {
      handleEnergySelection("Elec_Gas");
      setLPGValue(true);
      setInternetValue(true);
    } else {
      handleEnergySelection("");
    }
  }, [userProfile]);

  useEffect(() => {
    const address = getAddress();

    if (address) {
      if (isErgon(address)) {
        setIsErgonAddress(true);
        if (isErgonSolar(address)) {
          setIsErgonSolarPatch(true);
          setPlanFuel(FUEL_GAS);
        }
      } else {
        setIsErgonAddress(false);
        setIsErgonSolarPatch(false);
      }
    }
  }, [selectedAddress, unconfirmedAddress]);

  const getAddress = () => {
    return selectedAddress ?? unconfirmedAddress;
  };

  const getOriginAddressId = () => {
    return getAddress()?.originAddressId;
  };

  const handleValidate = () => {
    let error = "";

    setHasSubmitted(true);

    if (!getAddress()) {
      error += "Invalid address";
    }

    const fuelErrorMessage = getCheckboxErrorMessage(
      selectFuelCheckboxes.errors,
      isMoverPage,
      fuelValue,
      lpgValue,
      internetValue
    );
    error += fuelErrorMessage;
    setFuelError(fuelErrorMessage);

    return error;
  };

  const loadEnergyPlans = async (address: ConnectionAddressObject) => {
    const plans = await getCampaignPlans(
      address.originAddressId ?? "",
      address.elecDetails?.hasSolarPanels || false
    );
    setCampaignPlans(plans);
  };

  const loadLPGPlans = async (address: ConnectionAddressObject) => {
    try {
      const fetchedLPGMoveOffer = await getLPGOffer(
        address.qasAddress.city,
        address.qasAddress.postcode
      );
      setLpgOfferDeliverability(fetchedLPGMoveOffer);
      setLPGStates(
        fetchedLPGMoveOffer,
        lpgValue,
        !!fuelValue,
        internetValue,
        setLPGEnabled,
        setLPGValue,
        setDismissableBannerOptions
      );
    } catch (lpgError) {
      disableLPGSelection(setLPGEnabled, setLPGValue);
      const isLPGOnly = isLPGOnlySelected(fuelValue, lpgValue, internetValue);
      if (isLPGOnly) {
        setDismissableBannerOptions({
          type: DismissableBannerType.ERROR,
          message: "Sorry, something went wrong.",
          retryFn: () => window.location.reload(),
        });
      }

      errorForm({
        formName: FORM_NAME,
        errorMessage: isLPGOnly
          ? "Sorry, something went wrong."
          : "LPG checkbox disabled but no error was shown to customer",
        errorCode: (lpgError as Error)?.message || "Unable to fetch LPG offer",
      });
      logger.error("Unable to fetch LPG offer", {
        errorData: lpgError,
      });
    }
  };

  const handleAddressUpdate = async (address: ConnectionAddressObject) => {
    setGlobalError(false);
    removePlanSolar();

    await loadEnergyPlans(address);
    await loadLPGPlans(address);
  };

  const handleCheckboxTracking = (id: string, checked: boolean) => {
    interaction("click", {
      componentType: "Checkbox",
      id: `movers-widget-wp-checkbox-${id.toLowerCase()}`,
      parentContainer: "moving-home",
      appName: "movers wp",
      label: `book-my-move-${id.toLowerCase()}-${
        checked ? "checked" : "unchecked"
      }`,
      path: "/moving/",
    });
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { id, checked },
    } = e;
    setFuelError("");
    handleCheckboxTracking(id, checked);
    if (id === LPG) {
      setLPGValue(checked);
    } else if (id === INTERNET) {
      setInternetValue(checked);
    } else {
      const newFuelValue = getFuelName(id, fuelValue, checked);

      handleEnergySelection(newFuelValue);

      if (newFuelValue !== "") {
        // update Session Storage
        setPlanFuel(newFuelValue);
      }
    }
    setGlobalError(false);

    if (getAddress()?.qasAddress?.postcode) {
      setLPGStates(
        lpgOfferDeliverability,
        id === LPG ? checked : lpgValue,
        id === FUEL_ELEC || id === FUEL_GAS
          ? !!getFuelName(id, fuelValue, checked)
          : !!fuelValue,
        id === INTERNET ? checked : internetValue,
        setLPGEnabled,
        setLPGValue,
        setDismissableBannerOptions
      );
    }
  };

  const handleEnergySelection = (fuel: PlanFuelSelection | "") => {
    setFuelValue(fuel);

    // Sync fuel selection with Energy Address Lookup
    setSelectedEnergy({
      electricity: [FUEL_DUAL, FUEL_ELEC].includes(fuel),
      gas: [FUEL_DUAL, FUEL_GAS].includes(fuel),
    });
  };

  const handleSubmit = async (buttonValue: string) => {
    const originAddressId = getOriginAddressId();

    const isAddressFieldClear =
      !getAddress() && !originAddressId && !isAddressLookupInProgress;

    const selectedProducts = getSelectedProducts(
      fuelValue,
      lpgValue,
      internetValue
    );

    if (isAddressFieldClear) {
      if (buttonValue === BUTTON_CP) {
        logger.info(
          `Customer clicked COMPARE with [${selectedProducts}] selected`
        );
        return (window.location.href = getDestinationURLWhenNoAddress(
          fuelValue,
          lpgValue,
          internetValue
        ));
      }
    }

    if (handleValidate()) {
      return;
    }

    setPlanSolar(
      getAddress()?.elecDetails?.hasSolarPanels ? YesNoE.Yes : YesNoE.No
    );

    const useCart =
      selectedProducts.includes("Electricity") ||
      selectedProducts.includes("Gas");
    let cartFuel: IFuelTypes | undefined;

    if (useCart) {
      try {
        cartFuel = await createSalesCart(originAddressId!, {
          electricity: selectedProducts.includes("Electricity")
            ? campaignPlans?.electricity
            : undefined,
          gas: selectedProducts.includes("Gas")
            ? campaignPlans?.gas
            : undefined,
        });
        if (!cartFuel) {
          throw new Error("No eligible energy plans in the saved cart");
        }
        setPlanFuel(PLAN_FUEL_BY_CART_FUEL[cartFuel]);
      } catch (error) {
        setGlobalError(true);
        logger.error("Failed to create sales cart", error);
        return;
      }
    }
    if (
      buttonValue === BUTTON_SALES &&
      customisationRedirectEnabled &&
      useCart
    ) {
      logger.info(`Customer clicked MOVE with [${selectedProducts}] selected`);

      return (window.location.href = getSalesCustomisationURL(
        {
          internet: internetValue,
          lpg: lpgValue,
        },
        cartFuel
      ));
    }

    if (userProfile && buttonValue === BUTTON_SALES) {
      logger.info(`Customer clicked MOVE with [${selectedProducts}] selected`);

      return (window.location.href = getAuthMoveURL(
        userProfile,
        getAddress(),
        nmiResolutionState,
        useCart
      ));
    }

    return (window.location.href = getDestinationURL(
      buttonValue,
      fuelValue,
      lpgValue,
      internetValue,
      originAddressId!,
      useCart
    ));
  };

  const isCES = nmiResolutionState === NMIResolutionState.CES;

  const isElectricityRestricted =
    nmiResolutionState === NMIResolutionState.NmiHardStop || isErgonSolarPatch;

  const isAddressLookupLoading = isAddressLookupInProgress;

  const campaignEnergyPlansEmpty =
    !campaignPlans?.gas && !campaignPlans?.electricity;

  return (
    <StackWithWordpressOverrides data-id="movers-widget-wp" space="small">
      {!userProfile && (
        <>
          <Text>{selectFuelCheckboxes.label}</Text>
          <FormControl error={!!fuelError}>
            <div onChange={handleCheckboxChange}>
              <CheckboxGroup id="fuel-selector" error={!!fuelError}>
                <Inline>
                  <Checkbox
                    data-id={`checkbox-${FUEL_ELEC.toLowerCase()}`}
                    id={FUEL_ELEC}
                    label="Electricity"
                    checked={
                      !isElectricityRestricted &&
                      (fuelValue === FUEL_ELEC || fuelValue === FUEL_DUAL)
                    }
                    disabled={isElectricityRestricted}
                  />
                  <Checkbox
                    data-id={`checkbox-${FUEL_GAS.toLowerCase()}`}
                    id={FUEL_GAS}
                    label="Natural Gas"
                    checked={fuelValue === FUEL_GAS || fuelValue === FUEL_DUAL}
                  />
                  <Checkbox
                    data-id={`checkbox-${LPG.toLowerCase()}`}
                    id={LPG}
                    label={LPG}
                    checked={lpgValue}
                    disabled={!lpgEnabled}
                  />
                  <Checkbox
                    data-id={`checkbox-${INTERNET.toLowerCase()}`}
                    id={INTERNET}
                    label={INTERNET}
                    checked={internetValue}
                  />
                </Inline>
              </CheckboxGroup>
            </div>
            {!!fuelError && (
              <Text
                data-id="movers-widget-wp-fuel-error"
                variant="body-small"
                tone="critical"
              >
                {fuelError}
              </Text>
            )}
          </FormControl>
        </>
      )}
      <DismissableBanner
        {...dismissableBannerOptions}
        setDismissableBannerOptions={setDismissableBannerOptions}
      />

      <EnergyAddressLookup
        onAddressSelected={handleAddressUpdate}
        onAddressUnconfirmed={handleAddressUpdate}
        label="New connection address"
        searchByMirn={false}
        showDefaultPricing={false}
        showEmptyAddressError={hasSubmitted && !getAddress()}
      />
      <EnergyAddressLookup.BelowThresholdAlert />
      <EnergyAddressLookup.HardStopAlert />
      {globalError && !(isErgonAddress && campaignEnergyPlansEmpty) && (
        <LookupErrorAlert />
      )}
      <ErgonPatchAlert
        isErgon={isErgonAddress}
        isErgonSolar={isErgonSolarPatch}
        selectedFuel={{
          electricity: [FUEL_ELEC, FUEL_DUAL].includes(fuelValue),
          gas: [FUEL_GAS, FUEL_DUAL].includes(fuelValue),
        }}
        emptyPlans={campaignEnergyPlansEmpty}
      />
      {[
        NMIResolutionState.Init,
        NMIResolutionState.CES,
        NMIResolutionState.Completed,
        NMIResolutionState.ExactMatchFound,
        NMIResolutionState.NmiHardStop,
      ].includes(nmiResolutionState) ? (
        <Buttons
          buttonLabelCP={
            isCES ? LEGACY_MOVE_CONTENT.buttonLabelCP : buttonLabelCP
          } // For CES we need to evaluate based on NMI resolution status
          buttonLabelSales={isCES ? null : buttonLabelSales}
          disableBookMyMove={isAddressLookupLoading}
          disableCP={isAddressLookupLoading}
          handleSubmit={(label) => handleSubmit(label)}
        />
      ) : null}
      <Box paddingTop="medium">
        <Disclaimer />
      </Box>
    </StackWithWordpressOverrides>
  );
};
